import { useState, useEffect, useContext } from 'react';
import {
    Row,
    Col,
    Button,
    ButtonToolbar,
    Modal,
    ModalBody,
    Form,
    FormGroup,
    Label,
    Input,
    FormText,
} from 'reactstrap';
import classNames from 'classnames';
import { useForm, Controller } from 'react-hook-form';
import {
    fetchCampaignUpdatePresignedUrl,
    uploadFile,
} from '~/services/campaign';
import Dropzone from 'react-dropzone';
import toast from 'react-hot-toast';
import { CampaignUpdateFormModal } from '~/types/campaign';
import { RxCross2 } from 'react-icons/rx';
import { BiUpload } from 'react-icons/bi';
import { MdAdd } from 'react-icons/md';
import { set } from 'date-fns';
import TipTapEditor from '~/shared/components/tiptap/TipTapEditor';

export const CampaignUpdateModal = (props) => {
    const [savingUpdateImage, setSavingUpdateImage] = useState(false);
    const [showVideoField, setShowVideoField] = useState(false);

    const {
        color,
        colored,
        header,
        isOpen,
        toggle,
        onSubmit,
        campaignId,
        update,
    } = props;

    const defaultCampaignUpdate: CampaignUpdateFormModal = {
        body: '',
        image_url: '',
        video_url: '',
        campaign_id: campaignId,
    };

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { isSubmitting, isValid },
    } = useForm<CampaignUpdateFormModal>({
        defaultValues: defaultCampaignUpdate,
    });

    const imgUrl = watch('image_url');
    const videoUrl = watch('video_url');

    let Icon;

    switch (color) {
        case 'primary':
            Icon = <span className='lnr lnr-pushpin modal__title-icon' />;
            break;
        case 'success':
            Icon = <span className='lnr lnr-thumbs-up modal__title-icon' />;
            break;
        case 'warning':
            Icon = <span className='lnr lnr-flag modal__title-icon' />;
            break;
        case 'danger':
            Icon = <span className='lnr lnr-cross-circle modal__title-icon' />;
            break;
        default:
            break;
    }
    const getEmbeddedVideoUrl = (url: string) => {
        try {
            const youtubeRegex =
                /(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/i;
            const vimeoRegex =
                /(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_\-]+)?/i;

            // Check for YouTube URL
            const youtubeMatch = url.match(youtubeRegex);
            if (youtubeMatch && youtubeMatch[3]) {
                return `https://www.youtube.com/embed/${youtubeMatch[3]}`;
            }

            // Check for Vimeo URL
            const vimeoMatch = url.match(vimeoRegex);
            if (vimeoMatch && vimeoMatch[1]) {
                return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
            }

            // Return original URL for unsupported platforms
            return url;
        } catch (error) {
            console.error('Invalid video URL:', error);
            return url; // Fallback to the original URL
        }
    };

    const modalClass = classNames({
        'modal-dialog--colored': colored,
        'modal-dialog--header': header,
    });

    useEffect(() => {
        if (update) {
            reset(update);
        } else {
            reset(defaultCampaignUpdate);
        }
    }, [update]);

    useEffect(() => {
        if (videoUrl) {
            setValue('video_url', getEmbeddedVideoUrl(videoUrl));
        }
    }, [videoUrl]);

    const handleDrop = async (files) => {
        files.forEach(async (file) => {
            try {
                setSavingUpdateImage(true);
                const { fields, url } = await fetchCampaignUpdatePresignedUrl(
                    file.name,
                    campaignId
                );
                await uploadFile(fields, url, file);
                const publicUrl = `${url}${fields.key}`;
                setValue('image_url', publicUrl);
                setSavingUpdateImage(false);
                toast.success('Image uploaded successfully!');
            } catch (error) {
                setSavingUpdateImage(false);
                toast.error('Failed to upload image.');
            }
        });
    };

    const ImgDropzone = () => {
        const handleDrop = async (files) => {
            files.forEach(async (file) => {
                try {
                    setSavingUpdateImage(true);
                    const { fields, url } =
                        await fetchCampaignUpdatePresignedUrl(
                            file.name,
                            campaignId
                        );
                    await uploadFile(fields, url, file);
                    const publicUrl = `${url}${fields.key}`;
                    setValue('image_url', publicUrl);
                    setSavingUpdateImage(false);
                    toast.success('Image uploaded successfully!');
                } catch (error) {
                    setSavingUpdateImage(false);
                    toast.error('Failed to upload image.');
                }
            });
        };

        return (
            <Dropzone
                onDrop={handleDrop}
                accept={{ 'image/*': ['.jpeg', '.jpg', '.png'] }}
                multiple={false}
            >
                {({ getRootProps, getInputProps }) => (
                    <Button
                        style={{ border: '2px solid' }}
                        color='outline'
                        size='sm'
                        {...getRootProps()}
                    >
                        <input {...getInputProps()} />
                        {savingUpdateImage ? 'Uploading' : 'Upload Image'}
                    </Button>
                )}
            </Dropzone>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            modalClassName={`ltr-support`}
            className={`modal-dialog--${color} ${modalClass}`}
            centered={true}
        >
            <ModalBody>
                <button
                    className='lnr lnr-cross modal__close-btn'
                    type='button'
                    onClick={toggle}
                />
                {header ? '' : Icon}
                <h3 className='text-modal modal__title'>
                    {update ? 'Edit' : 'Add'} Campaign Update
                </h3>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <FormGroup>
                        <Label>
                            Share your update:{' '}
                            <span className='required'>*</span>
                        </Label>
                        <Controller
                            control={control}
                            name='body'
                            rules={{ required: true }}
                            render={({
                                field: { onChange, value, onBlur },
                            }) => {
                                return (
                                    <TipTapEditor
                                        initialValue={value}
                                        onChange={onChange}
                                    />
                                );
                            }}
                        />
                    </FormGroup>
                    {/* <Row>
            <Col>
              <FormGroup>
                <Label>Add a Image</Label>
                <Controller
                  name="image_url"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <span style={{ display: "flex", gap: 8 }}>
                        <Input
                          disabled
                          type="text"
                          {...field}
                        />
                        <ImgDropzone />
                      </span>
                      <FormText color="muted">
                        Accepted formats: .jpeg, .jpg, .png
                      </FormText>
                    </div>
                  )}
                />

              </FormGroup>
            </Col>
          </Row> */}

                    {/* {!imgUrl && (
                        <Row>
                            <Col>
                                <FormGroup>
                                    <Label className='mb-1'>Add Photo</Label>
                                    <Controller
                                        name='image_url'
                                        control={control}
                                        render={({ field }) => (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '8px',
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'flex',
                                                        gap: 8,
                                                    }}
                                                >
                                                    <Input
                                                        type='text'
                                                        placeholder='filename.jpg'
                                                        readOnly
                                                        {...field}
                                                    />
                                                    <Button
                                                        size='sm'
                                                        color='outline-dark'
                                                        type='button'
                                                        onClick={() => {
                                                            document
                                                                .getElementById(
                                                                    'image-upload'
                                                                )
                                                                .click();
                                                        }}
                                                        disabled={
                                                            savingUpdateImage
                                                        }
                                                    >
                                                        <BiUpload
                                                            size={20}
                                                            className='mb-1'
                                                        />
                                                        Upload
                                                    </Button>
                                                    <input
                                                        id='image-upload'
                                                        type='file'
                                                        accept='.jpeg,.jpg,.png'
                                                        style={{
                                                            display: 'none',
                                                        }} // Hide the input element
                                                        onChange={async (e) => {
                                                            const file =
                                                                e.target
                                                                    .files[0];
                                                            if (file) {
                                                                await handleDrop(
                                                                    [file]
                                                                );
                                                            }
                                                            e.target.value = '';
                                                        }}
                                                    />
                                                </span>
                                                <FormText
                                                    color='muted'
                                                    style={{ margin: 0 }}
                                                >
                                                    File
                                                    requirements/restrictions
                                                </FormText>
                                            </div>
                                        )}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                    {imgUrl && (
                        <div>
                            <p
                                style={{
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem',
                                    marginBottom: '5px',
                                }}
                            >
                                Uploaded Photo
                            </p>
                            <div
                                style={{
                                    width: '200px',
                                    height: '150px',
                                    border: '1px solid #ccc',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#f9f9f9',
                                }}
                            >
                                <img
                                    src={imgUrl}
                                    alt='Uploaded preview'
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain',
                                    }}
                                />
                            </div>
                            <Button
                                size='sm'
                                color='outline-dark'
                                className='modal_cancel mt-2'
                                onClick={() => {
                                    setValue('image_url', '');
                                }}
                            >
                                <RxCross2
                                    size={18}
                                    style={{
                                        position: 'relative',
                                        top: '-1px',
                                    }}
                                />
                                Remove Image
                            </Button>
                            <p
                                className='mt-2 mb-4'
                                style={{ wordBreak: 'break-all' }}
                            >
                                {imgUrl}
                            </p>
                        </div>
                    )} */}

                    {!videoUrl && (
                        <Row>
                            <Col>
                                {showVideoField && (
                                    <FormGroup>
                                        <Label className='mb-1'>
                                            Video URL
                                        </Label>
                                        <Controller
                                            name='video_url'
                                            control={control}
                                            render={({ field }) => (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '8px',
                                                    }}
                                                >
                                                    <Input
                                                        type='text'
                                                        placeholder='http://'
                                                        {...field}
                                                    />

                                                    <FormText color='muted'>
                                                        Enter YouTube or Vimeo
                                                        URL
                                                    </FormText>
                                                </div>
                                            )}
                                        />
                                    </FormGroup>
                                )}
                                {!showVideoField && (
                                    <Button
                                        size='sm'
                                        color='outline-dark'
                                        className='modal_cancel w-100'
                                        type='button'
                                        onClick={
                                            () => setShowVideoField(true)
                                            //     setValue(
                                            //         'video_url',
                                            //         getEmbeddedVideoUrl(
                                            //             field?.value
                                            //         )
                                            //     )
                                        }
                                    // disabled={
                                    //     !field.value ||
                                    //     !field.value.trim()
                                    // }
                                    >
                                        <MdAdd size={20} className='mb-1' />
                                        Add Video
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    )}

                    {videoUrl && (
                        <div>
                            <p
                                style={{
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem',
                                    marginBottom: '5px',
                                }}
                            >
                                Uploaded Video
                            </p>
                            <div
                                style={{
                                    marginTop: '10px',
                                }}
                            >
                                <iframe
                                    width='300px'
                                    height='200px'
                                    src={videoUrl}
                                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                                    allowFullScreen
                                ></iframe>
                            </div>
                            <Button
                                size='sm'
                                color='outline-dark'
                                className='modal_cancel'
                                onClick={() => {
                                    setValue('video_url', '');
                                    // setValue('video', '');
                                }}
                            >
                                <RxCross2
                                    size={18}
                                    style={{
                                        position: 'relative',
                                        top: '-1px',
                                    }}
                                />
                                Remove Video
                            </Button>
                            <p className='mt-2'>{videoUrl}</p>
                        </div>
                    )}

                    <ButtonToolbar className='modal__footer'>
                        <Button
                            color='outline-dark'
                            className='modal_cancel'
                            onClick={() => {
                                toggle();
                                setShowVideoField(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            className='modal_ok'
                            outline={colored}
                            color='primary'
                            type='submit'
                            disabled={isSubmitting || !isValid}
                        >
                            {isSubmitting ? 'Saving' : update ? 'Save' : 'Post'}
                        </Button>
                    </ButtonToolbar>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default CampaignUpdateModal;
